import {
  FIRST_TIME,
  type Ball,
  type Player,
  type Possession,
  type ShotStyle,
} from "@golazo/shared";
import { clamp, len2 } from "../core/math.js";

/**
 * Golpeo de PRIMERA: rematar o pasar el balon que llega sin pararlo antes.
 *
 * Por que existe: el jugador ya pulsa K para disparar. Pero si el balon venia
 * hacia el y no era suyo, esa misma K hacia una ENTRADA — el gesto menos
 * natural posible sobre un balon que viene a tus pies. Ahora la tecla hace lo
 * que uno espera: golpearlo al vuelo. La entrada sigue estando para cuando el
 * balon es de otro, que es cuando de verdad se quiere entrar.
 *
 * Lo satisfactorio no es la potencia, es el TIMING: hay una ventana corta
 * alrededor del contacto y solo el instante bueno saca el golpeo limpio. Pegarle
 * pronto o tarde tambien golpea el balon (nunca se queda uno con la pierna en
 * el aire, que se lee como que el juego no responde), pero sale escupido.
 */
export interface FirstTimeWindow {
  /** Segundos que faltan para que el balon llegue al pie. */
  timeToContact: number;
  /** 0 = contacto pesimo, 1 = golpeo perfecto. */
  quality: number;
  /** Balon por encima del suelo: sale volea en vez de disparo raso. */
  airborne: boolean;
}

/**
 * ¿Esta el balon llegando a este jugador ahora mismo?
 *
 * Usa la velocidad RELATIVA: un balon que va a la misma velocidad que tu no se
 * te esta acercando por mucho que este cerca, y eso distingue el pase que
 * recibes del balon que ya llevas conduciendo.
 */
export function findFirstTimeWindow(
  ball: Ball,
  player: Player,
  possession: Possession,
): FirstTimeWindow | null {
  if (possession.ownerId === player.id) return null;

  const dx = ball.pos.x - player.pos.x;
  const dy = ball.pos.y - player.pos.y;
  const rvx = ball.vel.x - player.vel.x;
  const rvy = ball.vel.y - player.vel.y;

  const closingSpeed = len2(rvx, rvy);
  if (closingSpeed < FIRST_TIME.MIN_CLOSING_SPEED) return null;

  // Componente de acercamiento: negativa = se acerca.
  const approach = dx * rvx + dy * rvy;
  if (approach >= 0) return null;

  const dist = len2(dx, dy);
  const reach = player.radius + ball.radius + FIRST_TIME.REACH;
  const timeToContact = Math.max(0, (dist - reach) / closingSpeed);
  if (timeToContact > FIRST_TIME.WINDOW) return null;

  // El balon tiene que venir MAS O MENOS a por ti, no cruzarte de largo.
  const closingRatio = -approach / (dist * closingSpeed || 1);
  if (closingRatio < FIRST_TIME.MIN_AIM) return null;

  // El punto dulce esta un pelin ANTES del contacto: como en el futbol real,
  // se arma el golpeo antes de que llegue, no cuando ya lo tienes encima.
  const error = Math.abs(timeToContact - FIRST_TIME.SWEET_SPOT);
  const quality = clamp(1 - error / FIRST_TIME.TOLERANCE, 0, 1);

  return {
    timeToContact,
    quality,
    airborne: ball.loft > FIRST_TIME.VOLLEY_HEIGHT,
  };
}

/** Estilo de remate segun como venga el balon. */
export function firstTimeShotStyle(window: FirstTimeWindow): ShotStyle {
  return window.airborne ? "volley" : "power";
}

/**
 * Potencia del golpeo de primera segun el timing.
 *
 * Un contacto perfecto pega MAS fuerte que un disparo cargado normal: es el
 * premio, y es lo que hace que valga la pena arriesgarse a no controlar. Uno
 * malo sale sin fuerza — el despeje flojo que cualquiera reconoce.
 */
export function firstTimePower(quality: number): number {
  return FIRST_TIME.POWER_MIN + (FIRST_TIME.POWER_MAX - FIRST_TIME.POWER_MIN) * quality;
}

/** Multiplicador de dispersion: el mal timing manda el balon a cualquier parte. */
export function firstTimeSpreadScale(quality: number): number {
  return FIRST_TIME.SPREAD_POOR + (FIRST_TIME.SPREAD_PERFECT - FIRST_TIME.SPREAD_POOR) * quality;
}

/** Etiqueta del contacto, para el feedback (sonido, particulas, camara). */
export type FirstTimeGrade = "perfecto" | "bueno" | "flojo";

export function gradeOf(quality: number): FirstTimeGrade {
  if (quality >= FIRST_TIME.PERFECT_AT) return "perfecto";
  if (quality >= FIRST_TIME.GOOD_AT) return "bueno";
  return "flojo";
}
